import type {FC} from 'react';
import clsx from 'clsx';
import Link from 'next/link';

import type {SearchResult} from '@/app/(frontend)/api/search/route';
import {Media} from '@/components/media';
import {documentToHref, formatDate} from '@/utils';
import {i18n} from '@/utils/i18n';

interface SearchItemProps {
	result: SearchResult;
}

export const SearchItem = ({result}: SearchItemProps) => {
	if (result === null) {
		return null;
	}

	const heading = result.heading?.at(0);
	const media = heading?._type === 'hero' ? heading?.media : undefined;
	const tagline = heading?._type === 'hero' ? heading?.tagline : undefined;
	let slug = undefined;
	if (result._type !== 'frontpage') {
		slug = result.slug?.current;
	}
	return (
		<li
			key={result._id}
			className={clsx(
				'grid',
				'grid-cols-1',
				'md:grid-cols-2',
				'lg:grid-cols-[1fr_2fr_150px]',
				'border-t',
				'border-gray-300',
				'pb-8',
				'gap-8',
				'pt-8',
			)}
		>
			<div className={clsx('h-full', 'w-full', 'min-w-full', 'relative')}>
				<div
					className={clsx(
						'block',
						'text-h4',
						'hover:underline',
						'underline-offset-[6px]',
						'decoration-2',
						'aspect-video',
						'w-full',
						'md:h-full',
						'min-h-[75px]',
						'md:min-h-[110px]',
						'lg:min-h-[165px]',
						'rounded-[20px]',
						'overflow-hidden',
						'relative',
					)}
				>
					<div
						className={clsx(
							'absolute',
							'inset-0',
							'overflow-hidden',
						)}
					>
						<Media
							media={media}
							disabledControls={true}
							disabledMask={true}
						/>
					</div>
				</div>
			</div>
			<div className={clsx('flex', 'flex-col', 'gap-8')}>
				<Link
					href={documentToHref(
						result.category.slug?.current,
						slug,
						result._type,
					)}
					className={clsx(
						'block',
						'text-h4',
						'hover:underline',
						'underline-offset-[6px]',
						'decoration-2',
					)}
				>
					{result.title}
				</Link>
				<div className={clsx('text-lg', 'leading-tight', 'flex-1')}>
					{tagline}
				</div>
				<div
					className={clsx(
						'flex',
						'justify-between',
						'items-center',
						'gap-8',
					)}
				>
					<div>
						{result.category?.title && (
							<div
								className={clsx(
									'text-center',
									'text-sm',
									'font-bold',
									'px-7',
									'py-2',
									'bg-green-100',
									'rounded-[10px]',
								)}
							>
								{result.category.title}
							</div>
						)}
					</div>
					<div className={clsx('block', 'lg:hidden')}>
						<ResultDetails result={result} />
					</div>
				</div>
			</div>
			<div className={clsx('hidden', 'lg:block')}>
				<ResultDetails result={result} />
			</div>
		</li>
	);
};

interface ResultDetailsProps {
	result: SearchResult;
}

const ResultDetails: FC<ResultDetailsProps> = ({result}) => {
	let type = '';

	switch (result._type) {
		case 'article':
			type = 'Artikkel';
			break;
		case 'category':
			type = 'Tema';
			break;
		case 'page':
			type = 'Side';
			break;
		case 'frontpage':
			type = 'Forside';
			break;
	}
	return (
		<div className={clsx('text-[10px]', 'font-medium')}>
			<div className={clsx('leading-tight')}>
				{i18n.components.searchPage.articleTypes}: {type}
			</div>
			{result._type === 'article' && (
				<div className={clsx('leading-tight')}>
					{i18n.components.searchPage.published}:{' '}
					{formatDate(result.publishDate ?? result._createdAt)}
				</div>
			)}
		</div>
	);
};
